import { ErrorBanner } from "@/components/board/ErrorBanner";
import { LoadingState } from "@/components/board/LoadingState";
import { Modal } from "@/components/Modal";
import { useToast } from "@/components/Toast";
import { useRecentlyChangedRowsApi } from "@/contexts/RecentlyChangedRowsContext";
import { useDealStages, type DealStage } from "@/contexts/DealStagesContext";
import { db, supabase } from "@/lib/supabase";
import { Pencil, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";

/** Parse a likelihood-to-close input: an integer 0-100, or null if invalid. */
function parseLikelihood(raw: string): number | null {
  if (!raw.trim()) return null;
  if (!/^\d+$/.test(raw)) return null;
  const value = Number(raw);
  if (!Number.isInteger(value) || value < 0 || value > 100) return null;
  return value;
}

/** Counts how many projects reference a deal stage. */
async function countDealStageUsage(stageId: string): Promise<number> {
  const { count, error } = await supabase
    .from("projects")
    .select("id", { count: "exact", head: true })
    .eq("deal_stage_id", stageId);
  // A Supabase query error resolves (it doesn't reject) with count: null, so we
  // surface it explicitly -- a failed count must not silently read as 0 and make
  // an in-use stage look deletable.
  if (error) throw error;
  return count ?? 0;
}

export function DealStagesSettings() {
  const { dealStages, loading, error, refresh } = useDealStages();
  const { addToast } = useToast();

  const [editing, setEditing] = useState<DealStage | null>(null);
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState<DealStage | null>(null);

  return (
    <section className="settings-section">
      <div className="settings-section__header">
        <h2 className="settings-section__title">Deal Stages</h2>
        <div className="settings-section__actions">
          <button type="button" className="primary-button" onClick={() => setAdding(true)}>
            Add Deal Stage
          </button>
        </div>
      </div>

      <p className="roles-settings__hint">
        Deal stages are ordered by likelihood to close. "Lost" and "Won" are reserved and can't be
        edited or deleted.
      </p>

      {error && <ErrorBanner message={error} entityName="deal stages" />}

      {loading ? (
        <LoadingState message="Loading deal stages…" />
      ) : (
        <div className="settings-table-wrapper">
          <table className="settings-table">
            <thead>
              <tr>
                <th className="settings-table__th">Deal Stage</th>
                <th className="settings-table__th">Likelihood to Close</th>
                <th className="settings-table__th settings-table__th--access">Actions</th>
              </tr>
            </thead>
            <tbody>
              {dealStages.map((stage) => {
                const reserved = stage.kind !== "open";
                const lockTitle = reserved ? "Reserved stage — can't be changed" : undefined;
                return (
                  <tr key={stage.id} className="settings-table__row">
                    <td className="settings-table__cell">{stage.name}</td>
                    <td className="settings-table__cell">{stage.likelihood_to_close}%</td>
                    <td className="settings-table__cell settings-table__cell--access">
                      <div className="roles-settings__row-actions">
                        <button
                          type="button"
                          className="icon-button"
                          onClick={() => setEditing(stage)}
                          disabled={reserved}
                          title={lockTitle}
                          aria-label={`Edit ${stage.name}`}
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          type="button"
                          className="icon-button"
                          onClick={() => setDeleting(stage)}
                          disabled={reserved}
                          title={lockTitle}
                          aria-label={`Delete ${stage.name}`}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {(adding || editing) && (
        <DealStageEditModal
          stage={editing}
          existing={dealStages}
          onClose={() => {
            setAdding(false);
            setEditing(null);
          }}
          onSaved={async () => {
            const wasEditing = !!editing;
            setAdding(false);
            setEditing(null);
            await refresh();
            addToast(wasEditing ? "Deal stage updated." : "Deal stage added.", "success");
          }}
        />
      )}

      {deleting && (
        <DealStageDeleteModal
          stage={deleting}
          otherStages={dealStages.filter((s) => s.id !== deleting.id)}
          onClose={() => setDeleting(null)}
          onDeleted={async () => {
            setDeleting(null);
            await refresh();
            addToast("Deal stage deleted.", "success");
          }}
        />
      )}
    </section>
  );
}

interface DealStageEditModalProps {
  stage: DealStage | null; // null = add
  existing: DealStage[];
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

function DealStageEditModal({ stage, existing, onClose, onSaved }: DealStageEditModalProps) {
  const { markSelfEdit } = useRecentlyChangedRowsApi();
  const [name, setName] = useState(stage?.name ?? "");
  const [likelihood, setLikelihood] = useState(stage ? String(stage.likelihood_to_close) : "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const parsedLikelihood = parseLikelihood(likelihood);
  const isValid = name.trim().length > 0 && parsedLikelihood !== null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid || saving) return;

    const trimmed = name.trim();
    const ltc = parsedLikelihood!;

    // Client-side distinctness checks for friendly errors; the DB unique
    // constraints on name and likelihood_to_close are the real backstop.
    const others = existing.filter((s) => s.id !== stage?.id);
    if (others.some((s) => s.name.toLowerCase() === trimmed.toLowerCase())) {
      setError(`A deal stage named "${trimmed}" already exists.`);
      return;
    }
    if (others.some((s) => s.likelihood_to_close === ltc)) {
      setError(`Another deal stage already has a ${ltc}% likelihood to close.`);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      if (stage) {
        markSelfEdit("deal_stages", stage.id);
        const { error: updateError } = await db.dealStages.update(stage.id, {
          name: trimmed,
          likelihood_to_close: ltc,
        });
        if (updateError) {
          setError(friendlyDealStageError(updateError.message, trimmed, ltc));
          return;
        }
      } else {
        const id = crypto.randomUUID();
        const { error: insertError } = await db.dealStages.create({
          id,
          name: trimmed,
          likelihood_to_close: ltc,
        });
        if (insertError) {
          setError(friendlyDealStageError(insertError.message, trimmed, ltc));
          return;
        }
        markSelfEdit("deal_stages", id);
      }
      await onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save deal stage");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={stage ? "Edit Deal Stage" : "Add Deal Stage"} onClose={onClose} size="small">
      <form className="role-form" onSubmit={handleSubmit}>
        {error && (
          <div className="error-banner">
            <span className="error-banner__message">{error}</span>
          </div>
        )}

        <div className="form-field">
          <label className="form-label" htmlFor="deal-stage-name">
            Name <span className="required">*</span>
          </label>
          <input
            id="deal-stage-name"
            type="text"
            className="form-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Negotiation"
            autoFocus
          />
        </div>

        <div className="form-field">
          <label className="form-label" htmlFor="deal-stage-likelihood">
            Likelihood to Close <span className="required">*</span>
          </label>
          <input
            id="deal-stage-likelihood"
            type="number"
            className="form-input"
            value={likelihood}
            onChange={(e) => setLikelihood(e.target.value)}
            min="0"
            max="100"
            step="1"
            placeholder="0-100"
          />
        </div>

        <div className="modal-actions">
          <button type="button" className="secondary-button" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className="primary-button" disabled={!isValid || saving}>
            {saving ? "Saving…" : stage ? "Save" : "Add Deal Stage"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

interface DealStageDeleteModalProps {
  stage: DealStage;
  otherStages: DealStage[];
  onClose: () => void;
  onDeleted: () => void | Promise<void>;
}

function DealStageDeleteModal({ stage, otherStages, onClose, onDeleted }: DealStageDeleteModalProps) {
  const { markSelfEdit } = useRecentlyChangedRowsApi();
  const [usage, setUsage] = useState<number | null>(null);
  const [usageError, setUsageError] = useState(false);
  const [reassignTo, setReassignTo] = useState(otherStages[0]?.id ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load usage count on open.
  useEffect(() => {
    let cancelled = false;
    countDealStageUsage(stage.id)
      .then((c) => {
        if (cancelled) return;
        setUsage(c);
        setUsageError(false);
      })
      // If we can't determine usage, block deletion rather than assuming the stage
      // is unused -- deleting an in-use stage would hit the projects FK.
      .catch(() => !cancelled && setUsageError(true));
    return () => {
      cancelled = true;
    };
  }, [stage.id]);

  const inUse = usage !== null && usage > 0;

  const handleDelete = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      if (inUse) {
        if (!reassignTo) {
          setError("Choose a deal stage to reassign to.");
          return;
        }
        const reassigned = await reassignDealStage(stage.id, reassignTo, markSelfEdit);
        if (!reassigned.ok) {
          setError(reassigned.message);
          return;
        }
      }
      markSelfEdit("deal_stages", stage.id);
      const { error: deleteError } = await db.dealStages.delete(stage.id);
      if (deleteError) {
        setError(deleteError.message);
        return;
      }
      await onDeleted();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete deal stage");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Delete "${stage.name}"`} onClose={onClose} size="small">
      <div className="role-form">
        {error && (
          <div className="error-banner">
            <span className="error-banner__message">{error}</span>
          </div>
        )}

        {usageError ? (
          <p>Couldn't check where this deal stage is used. Try again before deleting.</p>
        ) : usage === null ? (
          <p>Checking where this deal stage is used…</p>
        ) : inUse ? (
          <>
            <p>
              "{stage.name}" is used by {usage} project{usage === 1 ? "" : "s"}. Reassign them to
              another deal stage before deleting.
            </p>
            <div className="form-field">
              <label className="form-label" htmlFor="reassign-to">
                Reassign to <span className="required">*</span>
              </label>
              <select
                id="reassign-to"
                className="form-input"
                value={reassignTo}
                onChange={(e) => setReassignTo(e.target.value)}
              >
                {otherStages.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
          </>
        ) : (
          <p>Are you sure you want to delete "{stage.name}"?</p>
        )}

        <div className="modal-actions">
          <button type="button" className="secondary-button" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="danger-button"
            onClick={handleDelete}
            disabled={busy || usageError || usage === null || (inUse && !reassignTo)}
          >
            {busy ? "Deleting…" : inUse ? "Reassign & Delete" : "Delete Deal Stage"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Reassigns every project using `fromId` to `toId` in a single bulk UPDATE
 * (one statement = one transaction), then the caller deletes the stage.
 */
async function reassignDealStage(
  fromId: string,
  toId: string,
  markSelfEdit: (table: string, id: string) => void
): Promise<{ ok: true } | { ok: false; message: string }> {
  // Pre-mark the affected projects as self-edits so the realtime handler treats
  // the (about-to-arrive) row updates as ours, not external changes.
  const { data: affected, error: fetchError } = await supabase
    .from("projects")
    .select("id")
    .eq("deal_stage_id", fromId);
  if (fetchError) return { ok: false, message: fetchError.message };
  for (const p of affected ?? []) {
    markSelfEdit("projects", p.id);
  }

  const { error } = await supabase
    .from("projects")
    .update({ deal_stage_id: toId })
    .eq("deal_stage_id", fromId);
  if (error) return { ok: false, message: error.message };

  return { ok: true };
}

function friendlyDealStageError(message: string, name: string, likelihood: number): string {
  if (/unique|duplicate key/i.test(message)) {
    // The likelihood unique constraint mentions "likelihood" in its name.
    if (/likelihood/i.test(message)) {
      return `Another deal stage already has a ${likelihood}% likelihood to close.`;
    }
    return `A deal stage named "${name}" already exists.`;
  }
  if (/check|range|between/i.test(message)) {
    return "Likelihood to close must be a whole number between 0 and 100.";
  }
  return message;
}
